<template>
  <v-dialog v-model="show" width="600" persistent>
    <v-form ref="form">
      <v-card>
        <v-card-title>
          Register for {{ course.title }}
          <v-spacer></v-spacer>
          <v-btn color="red" icon @click="show = false">
            <v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-card-text class="mt-5">
          <v-text-field
            label="Name"
            required
            outlined
            prepend-inner-icon="mdi-account"
            type="text"
            v-model="user.name"
            :rules="[rules.required, rules.name]"
          ></v-text-field>
          <v-text-field
            label="Email address"
            required
            outlined
            prepend-inner-icon="mdi-email"
            type="email"
            v-model="user.email_address"
            :rules="[rules.required, rules.email]"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="primary"
            class="d-block mx-auto"
            @click="register()"
            :loading="loading"
            :disabled="loading"
            >{{ btnText }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-form>
    <loader
      v-if="isPaymentLoading"
      type="overlay"
      text="Verifying payment..."
    ></loader>
  </v-dialog>
</template>

<script>
import { apiClient, handleError } from "../../common/api/api.middleware";
import { NOTIFICATION_TYPE } from "../../common/constants";
import {
  addDoc,
  coursePaymentsRef,
  courseRegistrationsRef,
  getDocs,
  query,
  where,
} from "../../config/firebase";
import { showToastNotification } from "../../plugins/vue-toast-notifications";
import {
  displayRazorpay,
  verifyRazorpayPayment,
} from "../../helpers/razorpay/razorpay";
import Loader from "../../components/shared/Loader.vue";
import { apis } from "../../common/api/api.constants";

export default {
  components: { Loader },
  props: ["course", "visible", "user"],
  data() {
    return {
      loading: false,
      isPaymentLoading: false,
      btnText: "Pay and Register",
      rules: {
        required: (value) => !!value || "This field is required.",
        name: (value) => /^[a-zA-Z\s]+$/.test(value) || "Name must be valid.",
        email: (value) => /.+@.+\..+/.test(value) || "Email must be valid.",
      },
    };
  },
  created() {
    this.checkIfCourseIsFree();
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) this.$emit("close");
      },
    },
  },
  methods: {
    saveUserDetails(userDetails) {
      return new Promise((resolve, reject) => {
        addDoc(courseRegistrationsRef, userDetails)
          .then(async () => {
            const payload = {
              userName: this.user.name,
              resourceName: this.course.title,
              type: "course-registration-success",
              phoneNumber: this.user.phone_number,
            };
            const response = await apiClient.post(
              apis.sendTextMessage,
              payload
            );
            if (response.status === 200) {
              showToastNotification(
                "Registration successful.",
                NOTIFICATION_TYPE.SUCCESS
              );
            } else {
              showToastNotification(
                "Registration successful. But failed to send message.",
                NOTIFICATION_TYPE.WARNING
              );
            }
            this.loading = false;
            this.$emit("close");
            resolve();
          })
          .catch((error) => {
            handleError(error);
            this.loading = false;
            reject(error);
          });
      });
    },
    checkIfCourseIsFree() {
      if (!this.course?.entryFee || parseInt(this.course.entryFee) === 0) {
        this.btnText = "Register";
      }
    },
    async register() {
      if (!this.$refs.form.validate()) {
        return;
      }
      this.loading = true;
      const isRegistered = this.checkIfAlreadyRegistered();
      if (isRegistered) {
        showToastNotification(
          "You are already registered for this course",
          NOTIFICATION_TYPE.INFO
        );
        this.loading = false;
        return;
      }
      if (!this.course?.entryFee || parseInt(this.course.entryFee) === 0) {
        await this.saveUserDetails({
          user_id: this.user.user_id,
          name: this.user.name,
          email_address: this.user.email_address,
          phone_number: this.user.phone_number,
          course_id: this.course.id,
          payment_status: "paid",
          registration_date: new Date(),
        });
        this.loading = false;
        this.$emit("close");
        return;
      }
      this.handlePayment();
    },
    checkPaymentStatus() {
      return new Promise((resolve, reject) => {
        let isPaymentDone = false;
        const q = query(
          coursePaymentsRef,
          where("user_id", "==", this.user.user_id),
          where("course_id", "==", this.course.id)
        );

        getDocs(q)
          .then((querySnapshot) => {
            if (!querySnapshot.empty) {
              isPaymentDone = true;
            }
            resolve(isPaymentDone);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async savePaymentDetails(paymentDetails) {
      // save payment details to database
      await addDoc(coursePaymentsRef, paymentDetails);
      showToastNotification(
        "Payment successful. Registration completed.",
        NOTIFICATION_TYPE.SUCCESS
      );
    },
    async handlePayment() {
      const isPaymentDone = await this.checkPaymentStatus();
      if (isPaymentDone) {
        showToastNotification(
          "You have already paid for this course",
          NOTIFICATION_TYPE.INFO
        );
        return;
      }

      const entryFee = parseInt(this.course?.entryFee) || 0;

      try {
        let data = {
          amount: entryFee,
          receipt: `course-${this.course.id}-${this.user.user_id}`,
          email: this.user.email_address,
          contact: this.user.phone_number,
          name: this.user.name,
        };

        const response = await displayRazorpay(data);
        let verificationPayload = {
          payment_id: response.data.payment.razorpay_payment_id,
          order_id: response.data.order.id,
          signature: response.data.payment.razorpay_signature,
        };
        this.isPaymentLoading = true;
        const verifyPayment = await verifyRazorpayPayment(verificationPayload);

        if (verifyPayment.data.isSignatureValid) {
          this.isPaymentLoading = false;

          showToastNotification("Payment verified.", NOTIFICATION_TYPE.SUCCESS);

          this.savePaymentDetails({
            user_id: this.user.user_id,
            course_id: this.course.id,
            razorpay_payment_id: response.data.payment.razorpay_payment_id,
            razorpay_signature: response.data.payment.razorpay_signature,
            razorpay_order_id: response.data.payment.razorpay_order_id,
            order_id: response.data.order.id,
            amount: entryFee,
            payment_date: new Date(),
          });

          this.saveUserDetails({
            user_id: this.user.user_id,
            name: this.user.name,
            email_address: this.user.email_address,
            phone_number: this.user.phone_number,
            course_id: this.course.id,
            payment_status: "paid",
            registration_date: new Date(),
          });
        } else {
          this.isPaymentLoading = false;
          showToastNotification(
            "Payment verification failed. Please try again",
            NOTIFICATION_TYPE.ERROR
          );
        }
      } catch (error) {
        this.isPaymentLoading = false;
        this.loading = false;
        handleError(error);
      }
    },
    checkIfAlreadyRegistered() {
      let isRegistered = false;
      const q = query(
        courseRegistrationsRef,
        where("user_id", "==", this.user.user_id),
        where("course_id", "==", this.course.id)
      );

      getDocs(q)
        .then((querySnapshot) => {
          if (!querySnapshot.empty) {
            isRegistered = true;
          }
          return isRegistered;
        })
        .catch((error) => {
          handleError(error);
        });
    },
  },
};
</script>

<style scoped>
</style>